import { Component, OnInit, OnDestroy, ComponentRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
// MSAL_TOGGLE_START
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
// MSAL_TOGGLE_END
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Variables, VariableType } from '~/core/classes/user/variables';
import {
    NotificationData,
    NotificationSnackbarComponent
} from '~/core/components/notification-snackbar/notification-snackbar.component';

import { GraphService } from '~/core/services/graph.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { AppConfigService } from '~/core/services/appconfig.service';

import { AppSettings } from '~/core/models/appsettings';

export interface Modal {
    type?: ComponentRef<any>;
    inputs?: any;
    outputs?: any
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})

export class AppComponent implements OnInit, OnDestroy {
    private appSettings: AppSettings = new AppSettings();
    private readonly _destroying$ = new Subject<void>();
    private notificationLaunchInProgress: boolean = false;
    private readonly firstLoginNotificationVersion: string = 'v1';
    private readonly firstLoginNotificationPrefix: string = 'Home.FirstLoginNotification';

    private readonly notificationData: NotificationData = {
        heading: 'Two new agents are now in IntelENGINE',
        subHeading: 'Get asset integrity insights in seconds and draft DRTs faster, with source-backed answers.',
        steps: [
            {
                tabLabel: 'RIMS Agent',
                tabIcon: '🛡️',
                description: "Ask about any asset in plain engineering language. You don't need to click through RIMS tabs, reports or dashboards.",
                bullets: [],
                coverageGroups: [
                    {
                        label: 'Reports',
                        icon: '📄',
                        items: [
                            'Equipment Datasheets (EDS)',
                            'Safety Instruction Sheets (SIS)',
                            'Pressure Vessel Asset Strategy Reports',
                            'Piping Asset Strategy Reports',
                            'UT / RT Corrosion Reports',
                            'UT / RT Corrosion Historical Reports',
                            'UT / RT Survey Reports'
                        ]
                    },
                    {
                        label: 'Tables',
                        icon: '📊',
                        items: [
                            'Maximo WO table',
                            'Details tables',
                            'Inspection & History Briefs tables'
                        ]
                    },
                    {
                        label: 'Reference documents',
                        icon: '📁',
                        items: [
                            'Reference documents (Pascagoula only)'
                        ]
                    }
                ],
                tip: 'Tip: Include the asset tag and refinery in your question so you get accurate results.',
                examples: [
                    'What damage mechanisms apply to E-2114A at the Pascagoula refinery?',
                    'Show me the corrosion trend for CML 006 on C-90 at the Richmond refinery.',
                    'How many times has the bundle been replaced on heat exchanger E-1619 at the Pascagoula refinery?'
                ]
            },
            {
                tabLabel: 'DRT Agent',
                tabIcon: '📋',
                description: 'Generate a Detailed Requirements Table (DRT) from a Job Log ID or an attached scope document.',
                bullets: [],
                featureBullets: [
                    {
                        lead: 'One-prompt generation',
                        text: 'type Generate DRT for <Job Log ID>, or attach a scope document and type Generate DRT'
                    },
                    {
                        lead: 'Review parameters before generation',
                        text: 'users confirm key parameters such as pipe spec, service, material, thickness and hydrotest, along with other key parameters, before the DRT is built'
                    },
                    {
                        lead: 'Wide coverage',
                        text: 'process information, welding, bakeout, DHT/ISR, PWHT, NDE, insulation, coating and more'
                    },
                    {
                        lead: 'Transparent output',
                        text: 'each value comes with AI reasoning, a citation, any conflicts and past EWO suggestions'
                    },
                    {
                        lead: 'Download to Excel',
                        text: 'in the original DRT format, ready to complete'
                    }
                ],
                tip: 'Coming soon: RIMS integration with the DRT Agent is currently in progress.',
                tipVariant: 'info',
                tipIcon: '↪️',
                examples: [
                    'Generate DRT for JL-PAS-HYDR3-256021',
                    'Generate DRT for JL-RIC-FCC01-227180'
                ]
            }
        ]
    };

    public error: string = "";
    public title: string = "";
    public expand: boolean = false;
    // Right sidebar expand state
    public expandRight: boolean = false;

    public showNav: boolean = true;
    public showFooter: boolean = true;
    public showRightNav: boolean = true;

    public layoutCssClass: string = "";

    public isLoggedIn: boolean = false;
    public isLoading: boolean = true;
    public showNotificationBlocker: boolean = false;

    // Alert
    public showAlert: boolean = false;
    public alertTitle: string = "Alert";
    public alertMessage: string = "";
    public alertContinueText: string = "OK";
    public alertCancelText: string = "";

    // Modal
    public modal: Modal | null = null;

    constructor(
        // MSAL_TOGGLE_START
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        // MSAL_TOGGLE_END
        private graphService: GraphService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private appSettingsService: AppSettingsService,
        private appConfigService: AppConfigService,
        private snackBar: MatSnackBar
    ) {
        window.onbeforeunload = null;
        sessionStorage.removeItem("__allowNavigate__");

        setTimeout(() => {
            if (this.isLoggedIn) {
                this.layoutCssClass = Variables.get("Nav.LayoutCssClass", VariableType.Local) ?? "";
                this.setLayoutClass(false);
            }
        }, 1500);
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        this.syncLayoutFromRoute();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._destroying$)
            )
            .subscribe(() => this.syncLayoutFromRoute());

        await this.appSettingsService.getSettings(true).then(async (appSettings: AppSettings) => {
            if (appSettings) {
                this.appSettings = appSettings;
                this.title = this.appSettings.title;
                this.showNav = this.appSettings.showNav;
                this.showFooter = this.appSettings.showFooter;

                // MSAL_TOGGLE_START
                this.getMSALAccounts();
                // MSAL_TOGGLE_END

            } else {
                this.error = "Unable to load application settings.";
                this.isLoading = false;
            }
        });
    }

    public ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public setLayoutClass(change: boolean = true) {
        if (change) {
            if (this.layoutCssClass !== "") {
                this.layoutCssClass = "";
            } else {
                this.layoutCssClass = "expand";
            }
        }

        if (this.layoutCssClass === "") {
            this.expand = false;
        } else {
            setTimeout(() => {
                this.expand = true;
            }, 100);
        }

        Variables.set("Nav.LayoutCssClass", this.layoutCssClass, VariableType.Local);
    }

    // Right sidebar layout toggle
    public setLayoutClassRight(change: boolean = true) {
        if (!this.showRightNav) {
            this.expandRight = false;
            Variables.set("NavRight.LayoutCssClass", "", VariableType.Local);
            return;
        }

        if (change) {
            this.expandRight = !this.expandRight;
        }

        Variables.set("NavRight.LayoutCssClass", this.expandRight ? "expand-right" : "", VariableType.Local);
    }

    public async reloadPage(page = "") {
        const currentUrl = page === "" ? (this.router.url || '/') : page;

        await this.router.navigateByUrl(currentUrl, { replaceUrl: true });
    }

    //*************************************************************************
    //  Alert Methods
    //*************************************************************************
    public alertContinue() {
        this.showAlert = false;
    }

    public alertCancel() {
        this.showAlert = false;
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private syncLayoutFromRoute(): void {
        let route = this.activatedRoute;

        while (route.firstChild) {
            route = route.firstChild;
        }

        const hideRightNav = !!route.snapshot.data?.['hideRightNav'];
        this.showRightNav = !hideRightNav;

        if (!this.showRightNav) {
            this.expandRight = false;
            Variables.set("NavRight.LayoutCssClass", "", VariableType.Local);
        }
    }

    // MSAL_TOGGLE_START
    private getMSALAccounts() {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                let account: AccountInfo | null = this.authService.instance.getActiveAccount();
    
                if (account === null) {
                    account = this.getAccountInfo();
                }
    
                this.setAccountInfo(account);
            });
    }
    // MSAL_TOGGLE_END

    // MSAL_TOGGLE_START
    private getAccountInfo() {
        let accounts = this.authService.instance.getAllAccounts();
        let account: AccountInfo | null = null;
    
        if (accounts.length > 0) {
            this.authService.instance.setActiveAccount(accounts[0]);
            account = accounts[0];
        }
    
        return account;
    }
    // MSAL_TOGGLE_END

    // MSAL_TOGGLE_START
    private async setAccountInfo(account: AccountInfo | null) {
        let allowAccess: boolean = true; // Allow access by default
        let roles: any = [];
        
        // Get role from appConfig (can be group ID or group name)
        const roleConfig = this.appConfigService.getConfig('role');

        if (roleConfig) {
            // Check if roleConfig is a GUID (group ID) or a name
            const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(roleConfig);
            
            if (isGuid) {
                // Use group ID directly
                const isInGroup = await this.graphService.checkUserInGroup(roleConfig);

                if (isInGroup) {
                    roles.push(roleConfig);
                    console.log('User has access - Role ID:', roleConfig);
                } else {
                    allowAccess = false;
                    console.log('User not in group ID:', roleConfig, '- access denied');
                }
            } else {
                // Search by group name
                const groupAccess = await this.graphService.getUserGroups(roleConfig);

                if (groupAccess?.value.length > 0) {
                    // User is in a group that matches the name
                    const groupId = groupAccess.value[0].id;
                    roles.push(groupId);
                    console.log('User has access - Role:', roleConfig, '| Group ID:', groupId);
                } else {
                    allowAccess = false;
                    console.log('User not in group:', roleConfig, '- access denied');
                }
            }
        }

        if (account) {
            await this.setUserInfo(account, roles);
            // Clear old settings and save fresh data
            this.appSettings.user.roles = roles;
            await this.appSettingsService.saveSettings(this.appSettings);
        }

        if (allowAccess) {
            this.layoutCssClass = "";
            this.isLoggedIn = true;
            this.showFirstLoginNotification(account);
        } else {
            // Clear user data when access is denied and redirect to access-denied page
            this.appSettings.user = null as any;
            await this.appSettingsService.saveSettings(this.appSettings);
            this.layoutCssClass = "noaccess";
            this.isLoggedIn = false;
            await this.router.navigate(['/access']);
        }

        this.isLoading = false;
    }
    // MSAL_TOGGLE_END

    private showFirstLoginNotification(account: AccountInfo | null): void {
        if (!account || this.notificationLaunchInProgress) {
            return;
        }

        const notificationStorageKey = this.getFirstLoginNotificationKey(account);

        if (notificationStorageKey === '') {
            return;
        }

        if (Variables.get(notificationStorageKey, VariableType.Local)) {
            return;
        }

        this.notificationLaunchInProgress = true;
        this.showNotificationBlocker = true;

        const snackbarRef = this.snackBar.openFromComponent(NotificationSnackbarComponent, {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['notification-snackbar-container'],
            data: this.notificationData
        });

        snackbarRef.afterDismissed()
            .pipe(take(1))
            .subscribe(({ dismissedByAction }) => {
                try {
                    if (dismissedByAction) {
                        try {
                            Variables.set(notificationStorageKey, true, VariableType.Local);
                        } catch (storageError) {
                            console.warn('Unable to persist first-login notification dismissal.', storageError);
                        }
                        return;
                    }

                    if (this.isLoggedIn && !Variables.get(notificationStorageKey, VariableType.Local)) {
                        setTimeout(() => this.showFirstLoginNotification(account), 0);
                    }
                } finally {
                    // Always release UI interactivity, even if storage throws for some users.
                    this.showNotificationBlocker = false;
                    this.notificationLaunchInProgress = false;
                }
            });
    }

    private getFirstLoginNotificationKey(account: AccountInfo): string {
        const userId = account.homeAccountId || account.localAccountId || account.username;

        if (!userId) {
            return '';
        }

        return `${this.firstLoginNotificationPrefix}:${this.firstLoginNotificationVersion}:${userId}`;
    }

    // MSAL_TOGGLE_START
    private async setUserInfo(account: any, roles: any) {
        account.photo = "";
    
        if (account.name) {
            const name: string[] = account.name.split(", ");
    
            if (name && name.length > 1) {
                const firstName: string = name[1].split(" (")[0].split(" [")[0];
                const lastName: string = name[0];
    
                account.firstName = firstName;
                account.lastName = lastName;
                account.name = `${firstName} ${lastName}`;
                account.initials = firstName.substring(0, 1).toUpperCase() + lastName.substring(0, 1).toUpperCase();
            }
        }
    
        account.roles = roles;
    
        this.appSettings.user = account;
    }
    // MSAL_TOGGLE_END
}
 